/**
 * Route table (§18). Vite + React-Router v7 data router (§3 recorded deviation
 * from Remix; same route shape). Auth gating (§18, §19.1) is enforced by the
 * <SessionProvider> + <RequireRole> guards (component-level), driven by GET /me
 * — the worker re-checks every request server-side, so these are a UX layer,
 * not the security boundary. Read-only increment: the pack station is a
 * placeholder and admin/shipments are scaffolds.
 */
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { SessionProvider, RequireRole } from "./components/auth.js";
import { AppLayout } from "./components/AppLayout.js";
import { Queue } from "./routes/Queue.js";
import { OrderDrawer } from "./routes/OrderDrawer.js";
import { Shipments } from "./routes/Shipments.js";
import { Login } from "./routes/Login.js";
import { Pack } from "./routes/Pack.js";
import { Batches } from "./routes/Batches.js";
import { BatchDetail } from "./routes/BatchDetail.js";
import { PackingSlip } from "./routes/print/PackingSlip.js";
import { PickingList } from "./routes/print/PickingList.js";
import { BatchPackingSlips } from "./routes/print/BatchPackingSlips.js";
import { AdminLayout } from "./routes/admin/AdminLayout.js";
import { Stores } from "./routes/admin/Stores.js";
import { Carriers } from "./routes/admin/Carriers.js";
import { Routing } from "./routes/admin/Routing.js";
import { Readiness } from "./routes/admin/Readiness.js";
import { TradeLanes } from "./routes/admin/TradeLanes.js";
import { Users } from "./routes/admin/Users.js";
import { Stations } from "./routes/admin/Stations.js";
import { Settings } from "./routes/admin/Settings.js";
import { Barcodes } from "./routes/admin/Barcodes.js";

const MANAGER_ROLES = ["ADMIN", "MANAGER"] as const;

/** Wraps the whole tree in the session context so guards can read GET /me. */
function Root() {
  return (
    <SessionProvider>
      <Outlet />
    </SessionProvider>
  );
}

function ManagerArea() {
  return (
    <RequireRole roles={[...MANAGER_ROLES]}>
      <AppLayout />
    </RequireRole>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: "/login", element: <Login /> },
      // Pack station (§18). PACKER lands here; MANAGER/ADMIN allowed for testing.
      {
        path: "/pack",
        element: (
          <RequireRole roles={["ADMIN", "MANAGER", "PACKER"]}>
            <Pack />
          </RequireRole>
        ),
      },
      {
        // Chrome-free printable documents (§12). Any operator role may print.
        path: "/print/packing-slip/:orderId",
        element: (
          <RequireRole roles={["ADMIN", "MANAGER", "PACKER"]}>
            <PackingSlip />
          </RequireRole>
        ),
      },
      {
        path: "/print/picking-list/:batchId",
        element: (
          <RequireRole roles={["ADMIN", "MANAGER", "PACKER"]}>
            <PickingList />
          </RequireRole>
        ),
      },
      {
        path: "/print/packing-slips/:batchId",
        element: (
          <RequireRole roles={["ADMIN", "MANAGER", "PACKER"]}>
            <BatchPackingSlips />
          </RequireRole>
        ),
      },
      {
        path: "/",
        element: <ManagerArea />,
        children: [
          { index: true, element: <Navigate to="/queue" replace /> },
          {
            path: "queue",
            element: <Queue />,
            children: [{ path: ":orderId", element: <OrderDrawer /> }],
          },
          { path: "shipments", element: <Shipments /> },
          { path: "batches", element: <Batches /> },
          { path: "batches/:id", element: <BatchDetail /> },
          {
            path: "admin",
            element: <AdminLayout />,
            children: [
              { index: true, element: <Navigate to="/admin/stores" replace /> },
              { path: "stores", element: <Stores /> },
              { path: "carriers", element: <Carriers /> },
              { path: "routing", element: <Routing /> },
              { path: "readiness", element: <Readiness /> },
              { path: "trade-lanes", element: <TradeLanes /> },
              { path: "users", element: <Users /> },
              { path: "stations", element: <Stations /> },
              { path: "barcodes", element: <Barcodes /> },
              { path: "settings", element: <Settings /> },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/queue" replace /> },
    ],
  },
]);
